import Vue from 'vue'

/**
 * Global whitespace trimming for text-like Vuetify inputs (task ordit1/tasks/tasks#55).
 *
 * A v-text-field / v-textarea value the user changed is trimmed when the field
 * is left (blur), on Enter (v-text-field only) and right after a paste; a
 * v-combobox value (single string or array of chips) is trimmed when a chip is
 * committed. Trimming on every keystroke is intentionally avoided: a trailing
 * space typed mid-sentence must survive until the user leaves the field.
 * A padded value that merely receives focus and loses it again is left alone,
 * so tabbing through a form never dirties it.
 *
 * The trimmed value is written back through Vuetify's `internalValue` setter,
 * which emits `input`, so v-model / wrapper `@input` chains and vee-validate
 * see the trimmed value like a normal user edit.
 *
 * Why the Vuetify methods are wrapped instead of listening to the component's
 * `blur` / `change` events: Vuetify emits `change` with the current
 * `lazyValue` — synchronously from `onKeyDown` on Enter, and from the
 * `isFocused` watcher right after `onBlur` — and a template `@change` listener
 * is registered before this mixin's `created` hook runs. A component-level
 * listener would therefore trim AFTER a `:value` + `@change` consumer already
 * received the untrimmed payload. Wrapping `onBlur` / `onKeyDown` trims
 * before `change` is emitted, and Vuetify then also records the trimmed value
 * as `initialValue`, so blur after Enter does not emit `change` a second time.
 */

const TRIMMED_COMPONENTS = ['v-text-field', 'v-textarea', 'v-combobox']
// Components whose native `$refs.input` holds the value itself. A v-combobox's
// native input holds `internalSearch`, not the (possibly array) value, so it
// gets neither the native hooks nor the paste hook.
const NATIVE_INPUT_COMPONENTS = ['v-text-field', 'v-textarea']
const ENTER_KEY_CODE = 13

const warnedComponents = new Set()

/**
 * Trims a string, or every string element of an array (dropping elements that
 * become empty). Any other value is returned untouched.
 */
export function trimValue(value) {
    if (typeof value === 'string') return value.trim()

    if (Array.isArray(value)) {
        return value
            .map((item) => (typeof item === 'string' ? item.trim() : item))
            .filter((item) => item !== '')
    }

    return value
}

/**
 * Shallow equality good enough to decide whether a trimmed value differs from
 * the original one (strings, arrays of strings/objects, anything else by identity).
 */
export function isSameValue(a, b) {
    if (a === b) return true
    if (!Array.isArray(a) || !Array.isArray(b)) return false
    if (a.length !== b.length) return false

    return a.every((item, index) => item === b[index])
}

function warnUnsupported(componentName, reason) {
    if (process.env.NODE_ENV === 'production') return
    if (warnedComponents.has(componentName)) return

    warnedComponents.add(componentName)
    // eslint-disable-next-line no-console
    console.warn(
        `[trimInput] ${componentName}: ${reason} — input trimming is disabled for this component. Check the Vuetify version.`
    )
}

Vue.mixin({
    created() {
        const componentName = this.$options.name

        if (!TRIMMED_COMPONENTS.includes(componentName)) return

        if (!('internalValue' in this)) {
            warnUnsupported(componentName, 'no `internalValue` computed')
            return
        }

        const applyTrim = () => {
            const current = this.internalValue
            const trimmed = trimValue(current)

            if (!isSameValue(current, trimmed)) this.internalValue = trimmed
        }

        if (!NATIVE_INPUT_COMPONENTS.includes(componentName)) {
            // v-combobox: VSelect.setValue emits `change` once the committed
            // chip / free text is already in the value.
            this.$on('change', applyTrim)
            return
        }

        const originalOnBlur = this.onBlur
        const originalOnKeyDown = this.onKeyDown

        if (
            typeof originalOnBlur !== 'function' ||
            typeof originalOnKeyDown !== 'function'
        ) {
            warnUnsupported(componentName, 'no `onBlur` / `onKeyDown` method')
            return
        }

        // Only a value the user changed during this focus is trimmed;
        // Vuetify sets `initialValue` from `lazyValue` on focus.
        const trimIfEdited = () => {
            if (this.lazyValue !== this.initialValue) applyTrim()
        }

        this.onBlur = (event) => {
            trimIfEdited()

            return originalOnBlur(event)
        }

        this.onKeyDown = (event) => {
            // Only a v-text-field commits on Enter; in a v-textarea Enter
            // inserts a newline and must not trim mid-edit.
            if (
                componentName === 'v-text-field' &&
                event.keyCode === ENTER_KEY_CODE &&
                !event.isComposing
            ) {
                trimIfEdited()
            }

            return originalOnKeyDown(event)
        }

        this.$once('hook:mounted', () => {
            const input = this.$refs.input

            if (!input || typeof input.addEventListener !== 'function') {
                warnUnsupported(componentName, 'no native `$refs.input`')
                return
            }

            // setTimeout, not $nextTick: the microtask checkpoint runs as soon
            // as this listener returns, i.e. BEFORE the browser performs the
            // paste default action; a macrotask runs after the text is
            // inserted and Vuetify's `input` handler stored it.
            const onPaste = () => setTimeout(applyTrim, 0)

            input.addEventListener('paste', onPaste)
            this.$once('hook:beforeDestroy', () =>
                input.removeEventListener('paste', onPaste)
            )
        })
    },
})
